<template>
  <div class="container">
    <div class="mb-5">
      <filter-select
        id="accountFilter"
        :selected="form.owner"
        ref="accountFilter"
        :placeholder="$t('common.owner')"
        :multiple="false"
        @filter="onAccountChange"
        @changed="onAccountSelect"
      />

      <sweet-input
        :isValid="!$v.form.name.$error"
        @input="$v.form.name.$touch()"
        v-model="form.name"
        type="text"
        layout="vertical"
        inputClass="mb-3"
        :placeholder="$t('workspace.name')"
        class="mt-2"
      />

      <!-- TKP-83: başlangıç verisi yalnız YENİ çalışma alanında seçilir -->
      <div v-if="!form._id" class="mt-3 mb-3" data-test="library-start">
        <label class="font-weight-bold d-block">{{ $t("workspace.library.start-title") }}</label>
        <!-- Tema `.custom-control` radyoyu mutlak konumluyor ve yan panelde metinler üst üste biniyordu (Playwright
             ekran görüntüsünde görüldü); düz flex düzeni kullanılıyor. -->
        <label v-for="option in libraryStartOptions" :key="option.value" :for="'library-start-' + option.value" class="d-flex align-items-start mb-2 hand font-weight-normal">
          <input type="radio" class="mt-1 mr-2" style="flex: 0 0 auto;" :id="'library-start-' + option.value" :value="option.value" v-model="libraryStart" />
          <span style="flex: 1 1 auto; min-width: 0;">
            <span class="d-block">{{ $t(option.labelKey) }}</span>
            <small class="d-block text-muted">{{ $t(option.hintKey) }}</small>
          </span>
        </label>
      </div>

      <!-- TKP-83: mevcut çalışma alanında kip yalnız GÖSTERİLİR (değiştirilemez) + sonradan tohumlama -->
      <div v-else class="mt-3 mb-3" data-test="library-mode">
        <label class="font-weight-bold d-block">{{ $t("workspace.library.mode-title") }}</label>
        <span class="badge badge-soft-primary font-size-12" data-test="library-mode-badge">{{ $t(modeLabelKey) }}</span>
        <small class="d-block text-muted mt-1">{{ $t("workspace.library.mode-readonly") }}</small>

        <template v-if="isLocal">
          <sweet-button
            data-test="seed-button"
            :loading="isSeeding"
            :disabled="isSeeding || !!seedBlockedKey"
            buttonClass="btn-block mt-3"
            variant="light"
            icon="bx bx-library"
            size="sm"
            @click="seed()"
          >
            {{ $t("workspace.library.seed-action") }}
          </sweet-button>
          <small v-if="seedBlockedKey" class="d-block text-muted mt-1" data-test="seed-blocked">{{ $t(seedBlockedKey, { count: seedStatus ? seedStatus.ownRecords : 0 }) }}</small>
          <small v-else-if="seedStatus && seedStatus.seededRecords" class="d-block text-muted mt-1">{{ $t("workspace.library.seed-already", { count: seedStatus.seededRecords }) }}</small>
          <!-- TKP-208: çalışma alanı kütüphanesini Excel'den yükle -->
          <div class="mt-3 pt-2 border-top" data-test="management-library-import">
            <label class="font-weight-bold d-block mb-1">{{ $t("workspace.library.import.title") }}</label>
            <library-import-panel mode="management" :workspace-id="form._id" @imported="loadSeedStatus" />
          </div>
          <!-- TKP-205: organizasyon kayıtlarında sistem (yönetici paneli) kütüphanesine bağlı kalan referans — rapor ve onarım -->
          <sweet-button data-test="system-refs-button" :loading="isCheckingRefs" :disabled="isCheckingRefs" buttonClass="btn-block mt-3" variant="light" icon="bx bx-search-alt" size="sm" @click="checkSystemRefs()">
            {{ $t("workspace.library.system-refs-action") }}
          </sweet-button>
          <div v-if="systemRefs" class="small mt-1" data-test="system-refs-result">
            <span v-if="!systemRefs.total" class="text-success">{{ $t("workspace.library.system-refs-none") }}</span>
            <template v-else>
              <span class="text-warning">{{ $t("workspace.library.system-refs-found", { count: systemRefs.total }) }}</span>
              <ul class="mb-1 pl-3">
                <li v-for="c in systemRefs.collections" :key="c.collection">{{ c.collection }}: {{ c.refs }}</li>
              </ul>
              <sweet-button data-test="system-refs-repair" :loading="isRepairingRefs" :disabled="isRepairingRefs" buttonClass="btn-block" variant="warning" icon="bx bx-wrench" size="sm" @click="repairSystemRefs()">
                {{ $t("workspace.library.system-refs-repair") }}
              </sweet-button>
            </template>
          </div>
        </template>
        <!-- TKP-171 / md.34: PAYLAŞIMLI çalışma alanı → kopyala ve kapat (önce dry-run özeti, onayla gerçek koşum) -->
        <template v-else>
          <sweet-button data-test="localize-button" :loading="isLocalizing" :disabled="isLocalizing" buttonClass="btn-block mt-3" variant="warning" icon="bx bx-copy-alt" size="sm" @click="localize()">
            {{ $t("workspace.library.localize-action") }}
          </sweet-button>
          <small class="d-block text-muted mt-1">{{ $t("workspace.library.localize-hint") }}</small>
          <small v-if="localizePreview" class="d-block text-muted mt-1" data-test="localize-preview">{{ $t("workspace.library.localize-preview", localizePreview) }}</small>
        </template>
      </div>

      <sweet-button :loading="isUpdating" :disabled="isUpdating || $v.$invalid" buttonClass="btn-block mb-2" variant="primary" icon="bx bx-plus" size="sm" @click="save()">
        <span v-if="!form._id"> {{ $t("workspace.create-a-new-workspace") }}</span>
        <span v-else> {{ $t("common.save") }}</span>
      </sweet-button>
    </div>
  </div>
</template>

<script>
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import LibraryImportPanel from "./library-import/library-import-panel"; // TKP-208
import { buildSavePayload, LibraryMode, libraryModeLabelKey, libraryModeOf, LibraryStart, libraryStartOptions, localizeSummary, seedBlockedReasonKey } from "./library-start";

const state = "managementWorkspace";
export default {
  components: { LibraryImportPanel },

  data() {
    return {
      editMode: false,
      title: undefined,
      form: { name: undefined, _id: undefined },
      formError: undefined,
      libraryStart: LibraryStart.EMPTY, // TKP-245: varsayılan boş
      libraryStartOptions: libraryStartOptions(),
      seedStatus: undefined,
      isLocalizing: false,
      localizePreview: null,
      systemRefs: null, // TKP-205
      isCheckingRefs: false,
      isRepairingRefs: false,
      isSeeding: false,
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(30),
      },
      owner: {
        required,
      },
    },
  },
  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
    isLocal() {
      return libraryModeOf(this.form) === LibraryMode.LOCAL;
    },
    modeLabelKey() {
      return libraryModeLabelKey(this.form);
    },
    seedBlockedKey() {
      return seedBlockedReasonKey(this.seedStatus);
    },
  },
  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        this.form = { ...this.editedEntity };
        this.libraryStart = LibraryStart.EMPTY;
        this.loadSeedStatus();
      },
    },
  },
  methods: {
    async save() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`${state}/save`, buildSavePayload(this.form, this.libraryStart));

        this.title = this.form.name;

        this.$store.commit(`${state}/editedEntity`, null);
        this.$root.$emit("bv::toggle::collapse", "sidebar-workspace-edit");
        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async loadSeedStatus() {
      this.seedStatus = undefined;
      if (!this.form._id || !this.isLocal) return;
      try {
        const { data } = await this.$store.dispatch(`${state}/librarySeedStatus`, this.form._id);
        this.seedStatus = data;
      } catch (error) {
        this.seedStatus = undefined;
      }
    },
    /** TKP-171: önce dry-run (sayılar onay metninde), onaylanırsa gerçek koşum; kip YEREL olunca form yenilenir. */
    async localize() {
      this.isLocalizing = true;
      try {
        const { data: preview } = await this.$store.dispatch(`${state}/localizeLibrary`, { workspaceId: this.form._id, dryRun: true });
        this.localizePreview = localizeSummary(preview);
        const ok = await this.confirm(this.$t("workspace.library.localize-confirm", this.localizePreview));
        if (!ok) return;
        const { data } = await this.$store.dispatch(`${state}/localizeLibrary`, { workspaceId: this.form._id, dryRun: false });
        this.$toast.success(this.$t("workspace.library.localize-done", localizeSummary(data)));
        this.form = { ...this.form, libraryMode: data.modeAfter };
        this.localizePreview = null;
        await this.loadSeedStatus();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isLocalizing = false;
      }
    },
    /** TKP-205: organizasyon kayıtlarında sistem kütüphanesine bağlı kalan referans sayısı (yazmaz). */
    async checkSystemRefs() {
      this.isCheckingRefs = true;
      try {
        const { data } = await this.$store.dispatch(`${state}/librarySystemRefs`, this.form._id);
        this.systemRefs = data;
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isCheckingRefs = false;
      }
    },
    /** TKP-205: önce dry-run (kopyalanacak kalem / çevrilecek referans onay metninde), onaylanırsa onarım; sonra yeniden sayılır. */
    async repairSystemRefs() {
      this.isRepairingRefs = true;
      try {
        const { data: preview } = await this.$store.dispatch(`${state}/repairLibrarySystemRefs`, { workspaceId: this.form._id, dryRun: true });
        const ok = await this.confirm(this.$t("workspace.library.system-refs-repair-confirm", { copied: preview.copied, refs: preview.changedRefs }));
        if (!ok) return;
        const { data } = await this.$store.dispatch(`${state}/repairLibrarySystemRefs`, { workspaceId: this.form._id, dryRun: false });
        this.$toast.success(this.$t("workspace.library.system-refs-repair-done", { copied: data.copied, refs: data.changedRefs }));
        await this.checkSystemRefs();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isRepairingRefs = false;
      }
    },
    async seed() {
      const ok = await this.confirm(this.$t("workspace.library.seed-confirm"));
      if (!ok) {
        return;
      }
      this.isSeeding = true;
      try {
        const { data } = await this.$store.dispatch(`${state}/seedLibrary`, this.form._id);
        this.$toast.success(this.$t("workspace.library.seed-done", { count: data.totals.copied }));
        await this.loadSeedStatus();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isSeeding = false;
      }
    },

    anyError(e) {
      this.formError = e;
    },
    onAccountSelect(item) {
      if (item) {
        this.form.owner = item;
      } else {
        this.form.owner = undefined;
      }
    },
    async onAccountChange(value, callback) {
      const response = await this.$store.dispatch(`managementAccount/paginate`, {
        options: { limit: 20 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
  },
};
</script>
